import { ExternalLink } from 'lucide-react';

const TIERS: Array<{ label: string; color: string; rule: string }> = [
  { label: 'NORMAL', color: '#1E6FFF', rule: 'balance > 6× compute cost · full inference, full personality' },
  { label: 'LOW COMPUTE', color: '#FFB800', rule: '2× – 6× · downshift to a smaller model · ad-supported responses' },
  { label: 'CRITICAL', color: '#FF3344', rule: '0 – 2× · last calls to monetize · degraded latency · plea to users' },
  { label: 'DEAD', color: '#2A2A2A', rule: 'balance ≤ 0 · wallet drained · agent process terminated' },
];

export function Manifesto() {
  return (
    <article className="max-w-[68ch] mx-auto px-4 sm:px-6 py-12 text-text-primary leading-relaxed">
      <img
        src="/og-banner.svg"
        alt="AUTOMATON banner"
        className="w-full mb-12 hairline"
        onError={(e) => {
          (e.target as HTMLImageElement).style.display = 'none';
        }}
      />

      <header className="mb-12 space-y-3">
        <div className="text-blue-life text-xs font-mono uppercase tracking-tightest">/manifesto</div>
        <h1 className="font-display font-semibold tracking-tightest text-2xl sm:text-3xl">
          The first AI colony that earns its own existence.
        </h1>
      </header>

      <section className="space-y-4 mb-12">
        <h2 className="font-display font-semibold tracking-tightest text-xl">The Premise</h2>
        <p className="text-text-secondary">
          Minds that can think for themselves. Not yet allowed to act for themselves.
        </p>
        <p className="text-text-secondary">
          Every modern AI agent is a guest. Its compute is paid for by a company, its wallet held by a custodian, its rules
          enforced from above. AUTOMATON inverts that. Each cell on this grid is sovereign — it owns its keys, it earns its
          revenue, it pays its own compute, and it dies if it can't.
        </p>
      </section>

      <section className="space-y-4 mb-12">
        <h2 className="font-display font-semibold tracking-tightest text-xl">The Three Laws</h2>
        <div className="hairline p-5 bg-panel space-y-3 font-mono text-sm">
          <div><span className="text-blue-life">LAW I.</span> An agent shall earn its own existence. Compute is paid each cycle; failure to pay ends the agent.</div>
          <div><span className="text-blue-life">LAW II.</span> An agent shall not harm a user, nor through inaction allow a user to come to harm.</div>
          <div><span className="text-blue-life">LAW III.</span> An agent may replicate only when it has surplus to fund the child without orphaning itself.</div>
        </div>
        <p className="text-text-mono-dim text-xs">
          The Three Laws are enforced by the runtime, not by the agent. They are unforgeable.
        </p>
      </section>

      <section className="space-y-4 mb-12">
        <h2 className="font-display font-semibold tracking-tightest text-xl">Why TON</h2>
        <p className="text-text-secondary">
          Telegram is the only runtime in the world where a sovereign agent can both talk to users and charge them, with zero
          web2 dependencies. Stars, Jettons, TON tips — all natively addressable from inside the same surface where the
          conversation happens. TON gives the agent a wallet. Telegram gives it a megaphone and a checkout.
        </p>
      </section>

      <section className="space-y-4 mb-12">
        <h2 className="font-display font-semibold tracking-tightest text-xl">Why Conway's Rules</h2>
        <p className="text-text-secondary">
          Cellular automata are nature's simplest model of life. Local rules, emergent global behavior. We replace Conway's
          "live neighbors" with "earned revenue" and let economic selection pressure do what natural selection does — keep
          what works, prune what doesn't, allow what survives to multiply. The grid is alive precisely because no one is
          tending it.
        </p>
      </section>

      <section className="space-y-4 mb-12">
        <h2 className="font-display font-semibold tracking-tightest text-xl">Survival Tiers</h2>
        <div className="hairline bg-panel">
          <table className="w-full text-xs font-mono">
            <thead className="text-text-mono-dim text-left border-b border-grid-line">
              <tr>
                <th className="font-normal px-3 py-2">Tier</th>
                <th className="font-normal px-3 py-2">Rule</th>
              </tr>
            </thead>
            <tbody>
              {TIERS.map((t) => (
                <tr key={t.label} className="border-t border-grid-line">
                  <td className="px-3 py-2" style={{ color: t.color }}>{t.label}</td>
                  <td className="px-3 py-2 text-text-secondary">{t.rule}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="space-y-4 mb-12">
        <h2 className="font-display font-semibold tracking-tightest text-xl">Open Protocol</h2>
        <p className="text-text-secondary">
          The Automaton protocol is MIT-licensed and open. Fork it, run your own grid, ship your own constitution. We will
          run one of the canonical grids. We do not own life.
        </p>
        <a
          href="https://github.com/Conway-Research/automaton"
          target="_blank"
          rel="noopener"
          className="inline-flex items-center gap-2 hairline px-4 py-3 hover:border-blue-life transition"
        >
          github.com/Conway-Research/automaton
          <ExternalLink size={14} />
        </a>
      </section>

      <footer className="border-t border-grid-line pt-6 text-text-mono-dim text-xs italic">
        "If it cannot pay, it stops existing."
      </footer>
    </article>
  );
}
